import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Waves, Linkedin, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings } from '../services/publicContentService';

// Inline SVG marks for TikTok + X — Lucide doesn't ship these two.
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.86a8.16 8.16 0 0 0 4.77 1.52V7.94a4.86 4.86 0 0 1-1.84-1.25z" />
  </svg>
);
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2H21.5l-7.5 8.573L23 22h-6.844l-5.34-6.99L4.7 22H1.44l8.03-9.176L1 2h6.99l4.83 6.386L18.244 2Zm-1.2 18h1.84L7.05 3.9H5.09l11.954 16.1Z" />
  </svg>
);

// Default socials — used until the admin fills the contact_info settings.
// Facebook + YouTube are intentionally omitted because the company has no
// official accounts on those networks.
const DEFAULT_SOCIALS = [
  { id: 'linkedin',  label: 'LinkedIn',    href: 'https://www.linkedin.com/in/environmental-balance-88491a396?utm_source=share_via&utm_content=profile&utm_medium=member_ios', Icon: Linkedin },
  { id: 'instagram', label: 'Instagram',   href: 'https://www.instagram.com/ebalance_sa', Icon: Instagram },
  { id: 'tiktok',    label: 'TikTok',      href: 'https://vt.tiktok.com/ZSXVcBNxw/',      Icon: TikTokIcon },
  { id: 'twitter',   label: 'X (Twitter)', href: 'https://x.com/ebalance_sa',              Icon: XIcon },
];

// Company description (bilingual) — replaces the tagline for the footer only,
// so we don't have to touch the shared `mock.js.companyInfo` object.
const COMPANY_DESC = {
  ar: 'شركة التوازن البيئي تقدم حلولاً واستشارات ودراسات بيئية متكاملة وفق أعلى المعايير المهنية للمساهمة في تحقيق التنمية المستدامة.',
  en: 'Environmental Balance Company provides integrated environmental studies and consultancy services that support sustainable development through professional and innovative solutions.',
};

// Fixed year in the copyright per client spec (Arabic uses Eastern-Arabic digits).
const COPYRIGHT_YEAR = { ar: '٢٠٢٦', en: '2026' };

const TEXT = {
  ar: {
    quickLinks: 'روابط سريعة',
    // Mirrors the header navigation exactly — same labels, same routes.
    links: ['من نحن', 'خدماتنا', 'مركز الاعتمادات', 'الأخبار', 'تواصل معنا'],
    services: 'خدماتنا',
    servicesList: [
      'الدراسات البيئية',
      <>
        الاستشعار عن بعد ونظم
        <br />
        المعلومات الجغرافية
      </>,
      'التقييم والمراقبة',
      'الدراسات البحرية',
      'الاستشارات البيئية',
    ],
    contact: 'تواصل معنا',
    phone: '+966 012 6067393',
    address: 'ص.ب. 3500\nجدة 21471\nالمملكة العربية السعودية',
    email: 'info@ebe.com.sa',
    since: 'منذ',
    rights: 'جميع الحقوق محفوظة.',
    privacy: 'سياسة الخصوصية',
    madeBy: 'تم تصميم وتطوير الموقع بواسطة',
  },
  en: {
    quickLinks: 'Quick Links',
    links: ['About Us', 'Our Services', 'Accreditations Center', 'News', 'Contact Us'],
    services: 'Our Services',
    servicesList: [
      'Environmental Studies',
      'Remote Sensing and Geographic Information Systems',
      'Assessment & Monitoring',
      'Marine Studies',
      'Environmental Consultancy',
    ],
    contact: 'Contact Us',
    phone: '+966 012 6067393',
    address: 'P.O. Box 3500\nJeddah 21471\nKingdom of Saudi Arabia',
    email: 'info@ebe.com.sa',
    since: 'Since',
    rights: 'All Rights Reserved.',
    privacy: 'Privacy Policy',
    madeBy: 'Designed & Developed by',
  },
};

// Quick-link labels → routes. Every entry lines up with the header nav so
// clicking a footer link lands on the exact same page.
const QUICK_LINK_HREFS = {
  // Arabic labels
  'من نحن': '/',
  'خدماتنا': '/services',
  'مركز الاعتمادات': '/accreditations',
  'الأخبار': '/news',
  'تواصل معنا': '/contact',
  // English labels
  'About Us': '/',
  'Our Services': '/services',
  'Accreditations Center': '/accreditations',
  'News': '/news',
  'Contact Us': '/contact',
};

// Every Services list item points to the Services page. Kept simple per the
// spec — no deep-linking to specific study cards.
const SERVICES_HREF = '/services';

const Footer = ({ theme = 'light' }) => {
  const isDark = theme === 'dark';
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = isAr ? TEXT.ar : TEXT.en;
  const colAlign = isAr ? 'text-right' : 'text-left';

  const [contact, setContact] = useState(null);
  const [footerCms, setFooterCms] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('contact_info').then((v) => { if (alive) setContact(v || {}); });
    fetchPublicSettings('footer').then((v) => { if (alive) setFooterCms(v || {}); });
    return () => { alive = false; };
  }, []);

  const phone   = contact?.phone_primary || t.phone;
  const email   = contact?.email || t.email;
  const address = (isAr ? contact?.address_ar : contact?.address_en) || t.address;

  // CMS-driven link columns fall back to the built-in list when empty.
  const cmsQuickLinks = Array.isArray(footerCms.quick_links) ? footerCms.quick_links : [];
  const cmsServices   = Array.isArray(footerCms.services)    ? footerCms.services    : [];
  const quickLinks = cmsQuickLinks.length > 0
    ? cmsQuickLinks
    : t.links.map((label) => ({
        label_ar: label,
        label_en: label,
        href: QUICK_LINK_HREFS[label] || `#${label}`,
      }));
  const servicesLinks = cmsServices.length > 0
    ? cmsServices
    : t.servicesList.map((label) => ({ label_ar: label, label_en: label, href: SERVICES_HREF }));

  // Build socials: any URL provided by admin wins; missing ones fall back to defaults.
  const socials = DEFAULT_SOCIALS.map((s) => ({
    ...s,
    href: (contact && contact[s.id]) ? contact[s.id] : s.href,
  })).filter((s) => !!s.href);

  return (
    <footer dir={isAr ? 'rtl' : 'ltr'} className="relative overflow-hidden transition-colors duration-700">
      {/* Theme-aware background */}
      {isDark ? (
        <div className="absolute inset-0 bg-gradient-to-b from-black via-slate-950 to-black" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-50 via-blue-50 to-emerald-50" />
      )}

      {/* Decorative wave at top */}
      <div className="absolute top-0 left-0 right-0 h-32 overflow-hidden pointer-events-none">
        <svg className="absolute w-full h-full" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path
            d="M0,50 Q300,0 600,50 T1200,50 L1200,120 L0,120 Z"
            className={isDark ? 'fill-cyan-600/20' : 'fill-cyan-400/20'}
          />
          <path
            d="M0,70 Q300,20 600,70 T1200,70 L1200,120 L0,120 Z"
            className={isDark ? 'fill-blue-600/20' : 'fill-emerald-400/15'}
          />
        </svg>
      </div>

      <div className="relative container mx-auto px-4 pt-24 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-12">
          {/* Brand column */}
          <div className={`${colAlign} space-y-5`}>
            <div className={`flex items-center`}>
              <img
                src={companyInfo.logo}
                alt={companyInfo.name}
                style={{ minWidth: '150px' }}
                className={`h-30 w-auto max-w-none object-contain ${
                  isDark ? 'invert hue-rotate-180' : ''
                }`}
              />
            </div>
            <p className={`text-base md:text-lg leading-relaxed ${
              isDark ? 'text-cyan-200/80' : 'text-slate-600'
            }`} data-testid="footer-tagline">
              {isAr ? COMPANY_DESC.ar : COMPANY_DESC.en}
            </p>
            <div className={`flex items-center gap-2 text-base md:text-lg ${
              isDark ? 'text-cyan-300/60' : 'text-cyan-700/70'
            }`}>
              <span>{t.since} {companyInfo.foundedYear}</span>
              <Waves className="w-4 h-4" />
            </div>
          </div>

          {/* Quick links */}
          <div
            className={`${colAlign} space-y-4 ${
              isAr ? 'lg:-translate-x-14' : 'lg:translate-x-14'
            }`}
          >
            <h4 className={`text-xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>{t.quickLinks}</h4>
            <ul className="space-y-2" data-testid="footer-quick-links">
              {quickLinks.map((link, idx) => {
                const label = isAr ? (link.label_ar || link.label_en) : (link.label_en || link.label_ar);
                const href  = link.href || `#${label}`;
                return (
                  <li key={idx}>
                    <a
                      href={href}
                      className={`transition-colors duration-200 text-base md:text-base ${
                        isDark
                          ? 'text-cyan-200/70 hover:text-cyan-300'
                          : 'text-slate-600 hover:text-cyan-700'
                      }`}
                    >
                      {label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Services */}
          <div
            className={`${colAlign} space-y-4 ${
              isAr ? 'lg:mr-6' : 'lg:ml-6'
            }`}
          >
            <h4 className={`text-xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>{t.services}</h4>
            <ul className={`space-y-2 text-base md:text-base ${
              isDark ? 'text-cyan-200/70' : 'text-slate-600'
            }`} data-testid="footer-services-list">
              {servicesLinks.map((s, idx) => {
                const label = isAr ? (s.label_ar || s.label_en) : (s.label_en || s.label_ar);
                if (!s.href) return <li key={idx}>{label}</li>;
                return (
                  <li key={idx}>
                    <a href={s.href} className={`transition-colors duration-200 ${
                      isDark ? 'hover:text-cyan-300' : 'hover:text-cyan-700'
                    }`}>{label}</a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contact */}
          <div
            className={`${colAlign} space-y-4 ${
              isAr ? 'lg:-translate-x-14' : 'lg:translate-x-14'
            }`}
          >
            <h4 className={`text-xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>{t.contact}</h4>
            <div className="space-y-3">
              <div className={`flex items-center gap-3 text-base md:text-base ${
                isDark ? 'text-cyan-200/70' : 'text-slate-600'
              }`}>
                <Mail className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`} />
                <span data-testid="footer-email">{email}</span>
              </div>
              <div className={`flex items-center gap-3 text-base md:text-base
               ${
                isDark ? 'text-cyan-200/70' : 'text-slate-600'
              }`}>
                <Phone className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`} />
                <span data-testid="footer-phone">{phone}</span>
              </div>
              <div className={`flex items-start gap-3 text-base md:text-base ${
                isDark ? 'text-cyan-200/70' : 'text-slate-600'
              }`}>
                <MapPin className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`} />
                <span data-testid="footer-address" className="whitespace-pre-line leading-relaxed">{address}</span>
              </div>
            </div>

            {/* Social Icons — moved here from the Brand column so they sit
                directly below the contact info as a clean contact list. */}
            <div className="pt-4">
              <div className="flex items-center gap-2 flex-wrap" data-testid="footer-socials">
                {socials.map(({ id, label, href, Icon }) => (
                  <a
                    key={id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    data-testid={`social-${id}`}
                    className={`group relative w-10 h-10 rounded-full flex items-center justify-center
                              transition-all duration-300 hover:scale-110 hover:-translate-y-0.5
                              ${
                                isDark
                                  ? 'bg-white/[0.04] border border-cyan-500/20 hover:border-cyan-400/60 hover:bg-cyan-500/15'
                                  : 'bg-white border border-slate-200 hover:border-cyan-500/60 hover:bg-cyan-50 shadow-sm hover:shadow-md'
                              }`}
                  >
                    <Icon
                      className={`w-4.5 h-4.5 transition-colors duration-300 ${
                        isDark
                          ? 'text-cyan-300/80 group-hover:text-cyan-200'
                          : 'text-slate-500 group-hover:text-cyan-700'
                      }`}
                      style={{ width: '18px', height: '18px' }}
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className={`border-t pt-8 ${
          isDark ? 'border-cyan-800/30' : 'border-slate-200'
        }`}>
          <div className={`flex flex-col md:flex-row items-center justify-between gap-4 text-center ${isAr ? 'md:text-right' : 'md:text-left'}`}>
            <p className={`text-base md:text-lg ${isDark ? 'text-cyan-200/60' : 'text-slate-500'}`} data-testid="footer-copyright">
              © {isAr ? COPYRIGHT_YEAR.ar : COPYRIGHT_YEAR.en} {isAr ? companyInfo.name : companyInfo.nameEn}. {t.rights}
            </p>

            <Link
              to="/privacy-policy"
              data-testid="footer-privacy-link"
              className={`text-base md:text-lg font-semibold transition-colors ${
                isDark
                  ? 'text-cyan-200/70 hover:text-cyan-300'
                  : 'text-slate-600 hover:text-brand-blue-700'
              }`}
            >
              {t.privacy}
            </Link>

            <div className={`flex items-center gap-2 text-base md:text-lg ${
              isDark ? 'text-cyan-200/60' : 'text-slate-500'
            }`} data-testid="footer-credit">
              <span>{t.madeBy}</span>
              <span className={`font-bold tracking-wide ${
                isDark ? 'text-cyan-300' : 'text-brand-blue-700'
              }`}>KAFFAK Media</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-teal-500" />
    </footer>
  );
};

export default Footer;
